import React from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemButton, ListItemText, Stack, TextField, Typography } from '@mui/material';
import { templateService } from '../../lib/templateService';
import { buildTemplateContext } from '../../lib/buildTemplateContext';
import { extractTemplateVariables, interpolateTemplate, type TemplateContext } from '../../lib/templateInterpolation';
import { supabase } from '../../lib/supabase';
import { fetchLocatorProfile } from '../../services/emailService';
import type { EmailTemplate } from '../../types';
import type { SmsTemplateSelection } from '../../services/clientSmsService';

export function missingMessageVariables(body: string, context: TemplateContext): string[] {
  return extractTemplateVariables(body).filter(key => {
    const value = (context as Record<string, unknown>)[key];
    return value == null || String(value).trim() === '';
  });
}

interface Props {
  clientId: string;
  onClose: () => void;
  onInsert: (body: string, selection: SmsTemplateSelection) => void;
}

/** Shared template storage and interpolation; this picker only creates a draft. */
export default function SavedMessagePicker({ clientId, onClose, onInsert }: Props) {
  const [templates, setTemplates] = React.useState<EmailTemplate[]>([]);
  const [context, setContext] = React.useState<TemplateContext>({});
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');
  const [selected, setSelected] = React.useState<EmailTemplate | null>(null);
  const [required, setRequired] = React.useState<string[]>([]);
  const [values, setValues] = React.useState<Record<string, string>>({});
  const [attempt, setAttempt] = React.useState(0);
  React.useEffect(() => {
    let current = true;
    setLoading(true); setError(null);
    Promise.all([
      templateService.listTemplates('sms'),
      supabase.from('clients').select('first_name,last_name,min_budget,max_budget').eq('id', clientId).single(),
      fetchLocatorProfile(),
    ]).then(([rows, client, agent]) => {
      if (!current) return;
      if (client.error) throw new Error('Could not read client details. Try again.');
      setTemplates(rows.filter(row => row.body.trim()));
      setContext(buildTemplateContext(client.data, agent));
    }).catch(() => { if (current) setError('Could not load saved messages and client details. Try again.'); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [clientId, attempt]);

  const choose = (template: EmailTemplate) => {
    setError(null);
    if (!template.updated_at) { setError('This message has no saved version. Save it in Settings → Templates first.'); return; }
    setSelected(template); setValues({});
    setRequired(missingMessageVariables(template.body, context));
  };
  const resolvedContext = { ...context, ...values };
  const missing = selected ? missingMessageVariables(selected.body, resolvedContext) : [];
  const rendered = selected && missing.length === 0 ? interpolateTemplate(selected.body, resolvedContext).trim() : '';
  return <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="saved-message-title" PaperProps={{ sx: { m: 2, width: "calc(100% - 32px)" } }}>
    <DialogTitle id="saved-message-title">Insert a saved message</DialogTitle>
    <DialogContent>
      {loading ? <Typography role="status">Loading saved messages…</Typography> : null}
      {error ? <Alert severity="error" action={<Button onClick={() => setAttempt(value => value + 1)}>Retry</Button>}>{error}</Alert> : null}
      {!loading && !error && !selected ? <>
        <TextField autoFocus fullWidth label="Search saved messages" value={query} onChange={event => setQuery(event.target.value)} sx={{ mt: 1 }} />
        <List sx={{ maxHeight: '45vh', overflowY: 'auto' }}>
          {templates.filter(row => `${row.name} ${row.body}`.toLowerCase().includes(query.toLowerCase())).map(row =>
            <ListItemButton key={row.id} onClick={() => choose(row)}>
              <ListItemText primary={row.name} secondary={row.body.split('\n')[0]} secondaryTypographyProps={{ noWrap: true }} />
            </ListItemButton>)}
        </List>
        {templates.length === 0 ? <Typography>No saved SMS messages yet.</Typography> : null}
        <Typography variant="body2" color="text.secondary">Your saved messages and the team starter set are available here. Edit messages in Settings → Templates → SMS.</Typography>
      </> : null}
      {selected ? <Stack spacing={2} sx={{ mt: 1 }}>
        <Typography variant="subtitle1">{selected.name}</Typography>
        {required.length > 0 ? <Typography>Fill in these details before inserting:</Typography> : null}
        {required.map(key => <TextField key={key} required label={key === 'first_property_name' ? 'Property name' : key.replace(/_/g, ' ')} value={values[key] ?? ''} onChange={event => setValues(prev => ({ ...prev, [key]: event.target.value }))} />)}
        {rendered ? <Typography sx={{ whiteSpace: 'pre-wrap' }}>{rendered}</Typography> : null}
        {rendered.length > 1600 ? <Alert severity="warning">This message exceeds 1,600 characters. Shorten the saved template before inserting.</Alert> : null}
        <Typography variant="body2" color="text.secondary">You can edit this in the composer before sending.</Typography>
      </Stack> : null}
    </DialogContent>
    <DialogActions>
      {selected ? <Button onClick={() => { setSelected(null); setError(null); }}>Back</Button> : null}
      <Button onClick={onClose}>Cancel</Button>
      {selected ? <Button variant="contained" disabled={!rendered || missing.length > 0 || rendered.length > 1600} onClick={() => onInsert(rendered, { template_id: selected.id, template_version: selected.updated_at!, rendered_body: rendered })}>Insert into draft</Button> : null}
    </DialogActions>
  </Dialog>;
}
